import handorgel from 'handorgel';
import MicroModal from 'micromodal';
import { Fragment, FunctionComponent, h } from 'preact';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { getQueryString, filterQueryString, createQueryString } from '../../../src/ts/libs/queryStrings';
import { EndPointKeys, getApi } from '../../../src/ts/libs/getApi';
import { FilterItem, FilterOptionProps, Products, SearchResultsProps, ProductItemProps } from '../context/Product';
import { useFetch } from '../hooks/useFetch';
import { BasketItemProps } from '../types/basket';

interface ProductListComponentProps {
  categoryId?: string;
  api: EndPointKeys;
  pagination?: number;
  order?: string;
  hideFilterDropDown?: boolean;
  filterButtonLabel?: string;
  defaultSort?: string;
  showCategories?: boolean;
  loggedIn?: false;
  showAllButton?: boolean;
  alwaysShowFilters?: boolean;
  isSearchPage?: boolean;
  isCategoryPage?: boolean;
}

const addOrRemove = (arr, item) => (arr?.includes(item) ? arr?.filter((i) => i !== item) : [...arr, item]);

const addAccordionEvents = (filtersContainer) => {
  const config = {
    // whether multiple folds can be opened at once
    multiSelectable: true,
    // whether the folds are collapsible
    collapsible: true,

    // whether ARIA attributes are enabled
    ariaEnabled: true,
    // whether W3C keyboard shortcuts are enabled
    keyboardInteraction: true,
    // whether to loop header focus (sets focus back to first/last header when end/start reached)
    carouselFocus: true,

    // attribute for the header or content to open folds at initialization
    initialOpenAttribute: 'data-open',
    // whether to use transition at initial open
    initialOpenTransition: false,
    // delay used to show initial transition
    initialOpenTransitionDelay: 200,

    // header/content class if fold is open
    headerOpenClass: 'accordion__header--open',
    contentOpenClass: 'accordion__content--open',

    // header/content class if fold has been opened (transition finished)
    headerOpenedClass: 'accordion__header--opened',
    contentOpenedClass: 'accordion__content--opened',

    // header/content class if fold has been focused
    headerFocusClass: 'accordion__header--focus',
    contentFocusClass: 'accordion__content--focus',

    // header/content class if fold is disabled
    headerDisabledClass: 'accordion__header--disabled',
    contentDisabledClass: 'accordion__content--disabled',
  };

  try {
    setTimeout(() => {
      const acc = new handorgel(filtersContainer, config);
      // if (acc) {
      //   acc.update();
      // }
    }, 300);
  } catch (e) {
    console.warn(e);
  }
};

interface ProductTagProps extends FilterItem {
  handleRemove: any;
}

const CategoryButton = ({ isHidden, name, id }) => {
  const { handleSelectCategory, activeCategory } = useContext(Products);

  return (
    <li style={{ display: isHidden ? 'none' : 'list-item' }}>
      <button
        type="button"
        className={`button ${id === activeCategory ? 'button--primary is-active' : 'button--muted'} button--sm-on-sm`}
        onClick={() => handleSelectCategory(id)}
      >
        <span className="button__label">{name}</span>
      </button>
    </li>
  );
};

const CategoryButtons = ({ categories }) => {
  const [showMore, setShowMore] = useState(false);

  return (
    <ul className="button-list">
      {categories?.length >= 1 &&
        categories.map((category, index) => (
          <CategoryButton
            id={category.ID}
            name={category.Name}
            key={`cat-${category.ID}-${index}-${category.Count}`}
            isHidden={index >= 3 && !showMore}
          />
        ))}

      {categories?.length >= 3 && (
        <li>
          <button
            type="button"
            className="button button--outline button--muted button--sm-on-sm"
            onClick={() => setShowMore((m) => !m)}
          >
            <span className="button__label  ">{showMore ? 'Show less' : 'Show more'}</span>
            <svg aria-hidden="true" width={12} height={12} className="button__icon" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.333 7.037c0 .244-.2.444-.444.444H7.48v3.408c0 .244-.2.444-.444.444H4.963a.446.446 0 01-.444-.444V7.48H1.11a.446.446 0 01-.444-.444V4.963c0-.244.2-.444.444-.444H4.52V1.11c0-.244.2-.444.444-.444h2.074c.244 0 .444.2.444.444V4.52h3.408c.244 0 .444.2.444.444v2.074z"
                fill="currentColor"
                fillRule="evenodd"
              />
            </svg>
          </button>
        </li>
      )}
    </ul>
  );
};

export const InfoBlock = ({ message = null, children = undefined, error = false, test = '' }) => (
  <div className={`info-block info-block--${error ? 'error' : 'alert'}`} data-test={test}>
    <div className="info-block__icon">
      <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg">
        <path
          d="M21.667 11c0 5.892-4.776 10.667-10.667 10.667C5.11 21.667.333 16.892.333 11 .333 5.111 5.11.333 11 .333 16.89.333 21.667 5.111 21.667 11zM11 13.15a1.979 1.979 0 100 3.958 1.979 1.979 0 000-3.957zM9.122 6.04l.319 5.85a.516.516 0 00.515.487h2.088c.274 0 .5-.214.515-.488l.32-5.85a.516.516 0 00-.516-.543H9.637a.516.516 0 00-.515.544z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </svg>
    </div>
    <div className="info-block__text">{message || children}</div>
  </div>
);

const ProductTag: FunctionComponent<any> = ({ Name, ID, handleRemove }) => (
  <li>
    <button className="tag tag--product-tag" onClick={() => handleRemove(ID)}>
      {Name}
      <svg width={10} height={10} xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path fill="#F4F4F6" d="M-62-177h376v2336H-62z" />
          <path
            d="M5 0a5 5 0 00-5 5 5 5 0 005 5 5 5 0 005-5 5 5 0 00-5-5zm2.428 6.301a.24.24 0 010 .34l-.791.789a.24.24 0 01-.34 0L5 6.12l-1.3 1.31a.24.24 0 01-.34 0l-.789-.791a.24.24 0 010-.34l1.31-1.298-1.31-1.3a.24.24 0 010-.34l.791-.79a.24.24 0 01.34 0l1.298 1.312 1.3-1.31a.24.24 0 01.34 0l.79.79a.24.24 0 010 .34L6.118 5l1.31 1.3z"
            fill="#25252D"
          />
        </g>
      </svg>
    </button>
  </li>
);

const Checkbox = ({ filter: { ID, Name }, selectedFilters, handleToggle }) => (
  <div className="checkbox">
    <input
      name={ID}
      id={ID}
      type="checkbox"
      checked={selectedFilters?.includes(ID)}
      onChange={() => handleToggle(ID)}
    />
    <label htmlFor={ID}>{Name}</label>
  </div>
);

interface ModalProps {
  filters: FilterOptionProps[];
}
const Modal: FunctionComponent<ModalProps> = ({ filters }) => {
  const { selectedFilters, setSelectedFilters, handleResetSearch, loading } = useContext(Products);

  if (!filters) {
    return null;
  }
  const accordion = useRef(null);

  useEffect(() => {
    if (!accordion || !accordion.current || filters.length < 1) {
      return;
    }

    addAccordionEvents(accordion.current);
  }, [accordion, filters]);

  const handleToggle = (id) => {
    setSelectedFilters(() => addOrRemove(selectedFilters, id));
  };

  return (
    <div className="modal modal--blade  micromodal-slide " id="filter-blade" aria-hidden="true">
      <div className="modal__overlay" tabIndex={-1} data-micromodal-close>
        <div className="modal__container" role="dialog" aria-modal="true" aria-labelledby="filter-blade__heading">
          <header className="modal__header">
            <button
              type="button"
              className="button button--transparent  modal__close"
              aria-label="Close modal"
              data-micromodal-close
            >
              <span className="button__label  ">Close</span>
              <svg aria-hidden="true" width={24} height={24} xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 0C5.371 0 0 5.371 0 12s5.371 12 12 12 12-5.371 12-12S18.629 0 12 0zm5.884 15.15a.581.581 0 010 .823l-1.916 1.91a.581.581 0 01-.823 0L12 14.71l-3.15 3.174a.581.581 0 01-.823 0l-1.91-1.916a.581.581 0 010-.823L9.29 12 6.116 8.85a.581.581 0 010-.823l1.916-1.916a.581.581 0 01.823 0L12 9.291l3.15-3.175a.581.581 0 01.823 0l1.916 1.916a.581.581 0 010 .823L14.709 12l3.175 3.15z"
                  fill="currentColor"
                  fillRule="evenodd"
                />
              </svg>
            </button>
            <div className="modal__heading-wrap">
              <h2 className="modal__heading" id="filter-blade__heading">
                Filters
              </h2>
            </div>
            <div className="modal__header__components">
              <div className="filter-blade-header">
                <span className="filter-product-count" />
                <button
                  type="button"
                  className={`button button--transparent ${loading ? 'spin-icon' : ''}`}
                  onClick={handleResetSearch}
                >
                  <svg height={16} width={16} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                    <g fill="none" fillRule="evenodd">
                      <path
                        d="M11.7 4.041a5.38 5.38 0 00-3.705-1.46 5.426 5.426 0 00-5.251 4.092.388.388 0 01-.376.295H.519a.387.387 0 01-.38-.458 8.002 8.002 0 0113.387-4.294l1.152-1.152A.774.774 0 0116 1.61v4.324a.774.774 0 01-.774.775H10.9a.774.774 0 01-.547-1.322zM.775 9.291H5.1c.69 0 1.035.833.547 1.321L4.3 11.959a5.38 5.38 0 003.706 1.46 5.426 5.426 0 005.251-4.092.388.388 0 01.376-.295h1.849c.241 0 .425.22.38.458a8.002 8.002 0 01-13.387 4.294l-1.152 1.152A.774.774 0 010 14.39v-4.324c0-.428.347-.775.774-.775z"
                        fill="#25252d"
                      />
                    </g>
                  </svg>
                  <span className="button__label  ">Reset filters</span>
                </button>
              </div>
            </div>
          </header>
          <main className="modal__body" id="filter-blade-content">
            {/* {loading && <span>...Loading</span>} */}
            <div className="accordion accordion--bold filters-accordion" ref={accordion}>
              {filters
                .filter(({ items }) => items.filter(({ Disabled }) => !Disabled).length >= 1)
                .map(({ label, items }, index) => (
                  <Fragment key={`acc-${index}`}>
                    <h3 className="accordion__header">
                      <button className="accordion__header__button">
                        <span className="accordion__header__heading-wrap">
                          <span className="accordion__header__heading">{label}</span>
                        </span>
                        <svg aria-hidden="true" width={16} height={11} xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M7.15 9.98L.352 3.181c-.47-.47-.47-1.23 0-1.695l1.13-1.13c.47-.47 1.23-.47 1.695 0L8 5.171 12.818.352c.47-.47 1.23-.47 1.695 0l1.135 1.13c.47.47.47 1.23 0 1.695L8.85 9.974c-.47.475-1.23.475-1.7.005z"
                            fill="currentColor"
                            fillRule="evenodd"
                          />
                        </svg>
                      </button>
                    </h3>

                    <div className="accordion__content" data-open={index === 0}>
                      <div className="accordion__content__inner">
                        {items
                          .filter(({ Disabled }) => !Disabled)
                          .map((filter, idx) => (
                            <Checkbox
                              key={`filter-${index}-${idx}`}
                              filter={filter}
                              selectedFilters={selectedFilters}
                              handleToggle={handleToggle}
                            />
                          ))}
                      </div>
                    </div>
                  </Fragment>
                ))}
            </div>
          </main>
          <footer className="modal__footer">
            <button
              type="button"
              className={`button button--muted button--full ${loading ? 'is-loading' : ''}`}
              data-micromodal-close
            >
              <span className="button__label  ">Close filters</span>
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

const urlParts = window.location.href.split('/');
const isall = urlParts.includes('all');

export const ProductListWrap: FunctionComponent<ProductListComponentProps> = ({
  categoryId = '',
  api,
  pagination = 8,
  order = '',
  children,
  filterButtonLabel = 'Filters',
  hideFilterDropDown = false,
  defaultSort = 'popularity',
  showCategories,
  alwaysShowFilters,
  loggedIn = false,
  showAllButton = true,
  isSearchPage,
  isCategoryPage,
}) => {
  const firstLoad = useRef(true);
  const allowed = ['page', 'order', 's', 'filters', 'categoryId'];
  const searchRaw = window.location.search ? getQueryString(window.location.search) : {};

  const filteredSearch: any = filterQueryString(searchRaw, allowed);
  let filters = [];
  if (filteredSearch.filters) {
    filters = [...filteredSearch.filters.split(',')];
  }
  delete filteredSearch.filters;

  const searchPhrase = filteredSearch.s || '';

  const BASE_SEARCH = {
    page: 1,
    limit: isall ? 5000 : pagination,
    categoryId,
    order: defaultSort,
    search: searchPhrase,
    ...filteredSearch,
    filters,
  };

  console.log(BASE_SEARCH);

  const [search, setSearch] = useState(BASE_SEARCH);

  const [results, setResults] = useState<Partial<SearchResultsProps>>({});
  const [products, setProducts] = useState<ProductItemProps[]>([]);
  const [previousSelectedFilters, setPreviousSelectedFilters] = useState<any[]>([...BASE_SEARCH.filters]);
  const [selectedFilters, setSelectedFiltersOrg] = useState<any[]>([...BASE_SEARCH.filters]);
  const [basketItems, setBasketItems] = useState<BasketItemProps[]>([]);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  const [activeCategory, setActiveCategory] = useState<string>(BASE_SEARCH.categoryId);
  const [prevActiveCategory, setPrevActiveCategory] = useState<string>(BASE_SEARCH.categoryId);
  const { response, error, loading, fetchData, called } = useFetch(`${getApi(api)}`, {});

  const { response: basketResponse, fetchData: fetchBasket } = useFetch(`${getApi('getBasket')}`, {}, true);

  const setSelectedFilters = (data: any[]) => {
    setPreviousSelectedFilters(selectedFilters);
    setSelectedFiltersOrg(data);
  };

  // When the user uses the back / foward buttons listen for the state change.
  useEffect(() => {
    const popStateFunc = async (event) => {
      const searchRawNew = window.location.search ? getQueryString(window.location.search) : {};

      const filteredSearchNew: any = filterQueryString(searchRawNew, allowed);

      if (filteredSearchNew.categoryId) {
        setActiveCategory(filteredSearchNew.categoryId);
      } else {
        setActiveCategory('');
      }

      try {
        await handleSearch(
          { ...BASE_SEARCH, ...filteredSearchNew, page: 1, limit: +filteredSearchNew.page * 8 },
          [],
          null,
          true
        );

        // if (+filteredSearchNew?.page > 1) {
        //   setTimeout(() => {
        //     const cards = document.querySelectorAll('.product-card');

        //     const cardCount = +filteredSearchNew?.page + 7;

        //     if (cards?.length >= cardCount) {
        //       const card = cards[cardCount];

        //       if (card) {
        //         card.scrollIntoView({ behavior: 'auto' });
        //       }
        //     }
        //   }, 600);
        // }
      } catch (error) {
        console.log({ error });
      }
    };
    window.addEventListener('popstate', popStateFunc);

    return () => {
      window.removeEventListener('popstate', popStateFunc);
    };
  }, []);
  // If

  // compare 2 arrays to see if they are equal
  const compareArrays = (array1 = [], array2 = []) => {
    if (array1.length !== array2.length) {
      return false;
    }

    return array1.join() === array2.join();
  };

  const handleSearch = (searchData, selectedFiltersData, activeCategoryData, dontUpdateUrl = false) => {
    const data = { ...BASE_SEARCH, ...searchData, filters: selectedFiltersData };
    let page = data.page;
    if (activeCategoryData) {
      data.categoryId = activeCategoryData;
    }

    if (firstLoad.current && +filteredSearch.page > 1) {
      data.limit = pagination * +filteredSearch.page;
      data.page = 1;
    }

    if (activeCategoryData && activeCategoryData !== prevActiveCategory) {
      data.page = 1;
      page = 1;
      setPrevActiveCategory(activeCategoryData);
      setSearch((s) => ({
        ...s,
        page: 1,
      }));
    }

    const sameArray = compareArrays(selectedFiltersData, previousSelectedFilters);

    // if you change the filters, reset the page to 1
    if (!sameArray) {
      setPreviousSelectedFilters(selectedFiltersData);
    }
    if (!sameArray && data.page > 1) {
      page = 1;
      data.page = 1;
      setSearch((s) => ({
        ...s,
        page: 1,
      }));
    }

    if (data.categoryId) {
      data.categoryId = +data.categoryId;
    }
    if (!data.filters) {
      data.filters = [];
    }

    delete data.s;

    // make sure page is a number
    data.page = +data.page;

    fetchBasket();
    fetchData({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(data),
    });

    firstLoad.current = false;

    if (dontUpdateUrl) {
      return;
    }

    const newSearech = {
      ...searchData,
      page,
      filters: selectedFiltersData,
      categoryId: activeCategoryData,
    };

    const newQueryString = createQueryString(filterQueryString(newSearech, allowed));

    if (JSON.stringify(newSearech) !== JSON.stringify(BASE_SEARCH)) {
      history.pushState(null, null, `?${newQueryString}`);
    }
  };
  useEffect(() => {
    handleSearch(search, selectedFilters, activeCategory);
  }, [search, selectedFilters, activeCategory]);

  useEffect(() => {
    if (basketResponse && basketResponse.items) {
      setBasketItems(basketResponse.items);
    } else {
      setBasketItems([]);
    }
  }, [basketResponse]);
  useEffect(() => {
    if (response) {
      setResults(response);
    } else {
      // console.log('no response');
      return;
    }

    if (response && response?.page > 1 && response?.items?.length >= 1) {
      setProducts((p) => [...p, ...response.items]);
    } else {
      setProducts(response?.items?.length >= 1 ? response.items : []);
    }

    if (response && !selectedFilters) {
      const filteredIds = results?.filterOptions
        ?.map(({ items }) => items)
        .flat(1)
        .filter(({ Selected }) => !!Selected)
        .map(({ ID }) => ID);

      setSelectedFilters(filteredIds || []);
    }
    setLoadingMore(false);
  }, [response]);

  const filtered = useMemo(
    () =>
      results?.filterOptions
        ?.map(({ items }) => items)
        .flat(1)
        .filter(({ Selected }) => !!Selected),
    [results]
  );

  const pageUrl = window.location.href;
  const handleGetMore = (e) => {
    e.preventDefault();
    setLoadingMore(true);
    setSearch((s) => ({
      ...s,
      page: +s.page + 1,
    }));
  };
  const handleModalOpen = () => {
    MicroModal.show('filter-blade', { disableScroll: true });
  };

  const handleResetSearch = () => {
    setActiveCategory(categoryId);
    setSelectedFilters([]);
    setSearch({ ...BASE_SEARCH, order: defaultSort, categoryId, search: searchPhrase });
  };
  const handleViewAll = (e) => {
    setLoadingMore(true);
    e.preventDefault();

    setSearch({ ...BASE_SEARCH, order: defaultSort, categoryId, search: searchPhrase, limit: 5000 });
    setSelectedFilters([]);
    window.history.pushState({}, null, `${pageUrl}/all`);
  };

  const handleChangeOrder = (e) => {
    setSearch({
      ...BASE_SEARCH,
      categoryId,
      filters: selectedFilters,
      search: searchPhrase,
      order: e.target.value,
      page: 1,
    });
    // setSelectedFilters([]);
  };

  const handleRemoveSingleFilter = (id) => setSelectedFilters((arr) => arr.filter((i) => i !== id));

  const handleSelectCategory = (newCategoryId: string) => {
    setActiveCategory(newCategoryId);
  };

  useEffect(() => {
    window?.addEventListener('basket-remove-item', fetchBasket);
    return () => {
      // Clean up the subscription
      window?.removeEventListener('basket-remove-item', fetchBasket);
    };
  }, []);

  const categories = useMemo(
    () =>
      results?.filterCategories?.length >= 1
        ? results?.filterCategories.sort((a, b) => (a.Count < b.Count ? 1 : -1))
        : [],
    [results]
  );
  const activeCategoryObject = categories.find(({ ID }) => +ID === +activeCategory);

  const filterOptions = results?.filterOptions?.length
    ? results?.filterOptions.filter(({ items }) => items.length >= 1)
    : [];

  // rules to show the filters
  const showFilterButton = filterOptions?.length >= 1 && (!showCategories || activeCategory || alwaysShowFilters);
  console.log({
    categories,
    alwaysShowFilters,
    activeCategory,
    filterOptions,
    showFilterButton,
    activeCategoryObject,
    results,
    isCategoryPage,
    isSearchPage,
  });
  return (
    <Products.Provider
      value={{
        search,
        results,
        isLoggedIn: results?.loggedIn || loggedIn,
        products,
        selectedFilters,
        setSelectedFilters,
        handleSelectCategory,
        activeCategory,
        loading,
        error,
        handleResetSearch,
        setSearch,
        categories,
        basketItems,
        setBasketItems,
        fetchBasket,
        loadingMore,
      }}
    >
      <div className="product-list" data-test="product-list">
        <div className="product-list__header">
          <div className={`list-controls ${showCategories ? 'list-controls--categories' : ''}`}>
            <div
              className={`list-controls__filter-btn ${
                (filtered && filtered.length >= 1) || activeCategory ? 'list-controls__filter-btn--tags ' : ''
              }`}
            >
              {showFilterButton && (
                <Fragment>
                  <button
                    type="button"
                    className="button button--muted"
                    onClick={() => handleModalOpen()}
                    disabled={loading}
                    data-test="product-list-filters-button"
                  >
                    <svg width={19} height={16} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M.677 2.939h17.609a.665.665 0 00.677-.653V.653A.665.665 0 0018.286 0H.677A.665.665 0 000 .653v1.633c0 .36.303.653.677.653zm-.17 6.53h13.207c.28 0 .508-.292.508-.653V7.184c0-.361-.227-.653-.508-.653H.508c-.28 0-.508.292-.508.653v1.632c0 .361.227.653.508.653zM.34 16h8.804c.187 0 .338-.292.338-.653v-1.633c0-.36-.151-.653-.338-.653H.339c-.187 0-.339.293-.339.653v1.633c0 .36.152.653.339.653z"
                        fill="#FFF"
                        fillRule="evenodd"
                      />
                    </svg>
                    <span className="button__label">{filterButtonLabel}</span>
                  </button>
                  <Modal filters={filterOptions} />
                </Fragment>
              )}
            </div>
            {showCategories && !activeCategory && categories.length >= 1 && <CategoryButtons categories={categories} />}
            <div className="list-controls__reset-btn">
              <button
                type="button"
                className={`button button--transparent ${loading ? 'spin-icon' : ''}`}
                onClick={handleResetSearch}
              >
                <svg height={16} width={16} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <g fill="none" fillRule="evenodd">
                    <path
                      d="M11.7 4.041a5.38 5.38 0 00-3.705-1.46 5.426 5.426 0 00-5.251 4.092.388.388 0 01-.376.295H.519a.387.387 0 01-.38-.458 8.002 8.002 0 0113.387-4.294l1.152-1.152A.774.774 0 0116 1.61v4.324a.774.774 0 01-.774.775H10.9a.774.774 0 01-.547-1.322zM.775 9.291H5.1c.69 0 1.035.833.547 1.321L4.3 11.959a5.38 5.38 0 003.706 1.46 5.426 5.426 0 005.251-4.092.388.388 0 01.376-.295h1.849c.241 0 .425.22.38.458a8.002 8.002 0 01-13.387 4.294l-1.152 1.152A.774.774 0 010 14.39v-4.324c0-.428.347-.775.774-.775z"
                      fill="#25252d"
                    />
                  </g>
                </svg>
                <span className="button__label  ">Reset filters</span>
              </button>
            </div>

            {((filtered && filtered.length >= 1) || activeCategory) && (
              <div className="list-controls__filter-tags">
                <ul className="tag-list">
                  {!isCategoryPage && activeCategory && activeCategoryObject && (
                    <ProductTag {...activeCategoryObject} handleRemove={handleResetSearch} />
                  )}
                  {filtered?.length >= 1 &&
                    filtered.map((filter, index) => (
                      <ProductTag key={`tag-item-${index}`} {...filter} handleRemove={handleRemoveSingleFilter} />
                    ))}
                </ul>
              </div>
            )}

            {!loading && results?.total >= 1 && (
              <div className="list-controls__total" data-test="product-list-result-count">
                {results.total} results
              </div>
            )}

            {/* Conditionally render the filter drop down using the hideFilterDropDown prop */}
            {hideFilterDropDown ? null : (
              <div className="list-controls__sort">
                <div className="form-field">
                  <label className="form-field__label sr-only" htmlFor="sort">
                    Sort products
                  </label>
                  <select
                    name="sort"
                    id="sort"
                    className="select"
                    value={search.order}
                    onChange={handleChangeOrder}
                    data-test="product-list-change-order"
                  >
                    {(defaultSort === 'New-Old' || defaultSort === 'Old-New') && (
                      <Fragment>
                        <option value="New-Old">Newest</option>
                        <option value="Old-New">Oldest</option>
                      </Fragment>
                    )}
                    <option value="popularity">Popularity</option>
                    <option value="alphabeticallyAscending">Alphabetically Ascending</option>
                    <option value="alphabeticallyDescending">Alphabetically Descending</option>
                    <option value="priceAscending">Price Ascending</option>
                    <option value="priceDescending">Price Descending</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="product-list__body">
          {called && !loading && error && (
            <InfoBlock message="Sorry there was an error loading the product results" error test="product-list-error" />
          )}

          {called && !loading && !error && products && products?.length === 0 && (
            <InfoBlock message="No products found, please narrow your search" test="product-list-no-result" />
          )}

          {children}
        </div>

        {results?.total_pages >= 2 && (
          <div className="product-list__footer">
            <div className="pagination" data-test="product-list-pagination">
              <div className="pagination__text">
                Showing {products.length} of {results.total}
              </div>
              <span className="pagination__indicator">
                <span
                  className="pagination__indicator__bar"
                  style={{ width: `${(results.page / results.total_pages) * 100}%` }}
                />
              </span>

              {results.page < results.total_pages && (
                <button
                  type="button"
                  className={`button button--outline pagination__action ${loading ? 'is-loading' : ''}`}
                  onClick={(e) => handleGetMore(e)}
                >
                  <span className="button__label  ">Load more</span>
                  <svg aria-hidden="true" width={6} height={8} xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.49 3.575L2.09.176a.597.597 0 00-.846 0L.679.741a.597.597 0 000 .847L3.085 4 .676 6.41a.597.597 0 000 .846l.565.568a.597.597 0 00.847 0l3.4-3.4a.598.598 0 00.002-.849z"
                      fill="currentColor"
                      fillRule="evenodd"
                    />
                  </svg>
                </button>
              )}

              {showAllButton && (
                <div>
                  <a
                    href={`${pageUrl}/all`}
                    class="button button--link"
                    onClick={handleViewAll}
                    data-test="product-list-view-all"
                  >
                    <span class="button__label  ">View All</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Products.Provider>
  );
};
